import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMoodleV2 } from "../../../contexts/moodle";
import { useAuth } from "../../../contexts/AuthContext";
import { requestAutoLogin } from "../../../services/moodle/userauth";
import { baseURL } from "../../../services/apirest";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import * as d3 from "d3";

export const ProgressRowItem = ({ course, grade, lang }) => {
  const navigation = useNavigate();
  const moodlev2 = useMoodleV2();
  const { user } = useAuth();
  const [data /* setData */] = React.useState({
    name: course.displayname,
    start: new Date(course.startdate * 1000).toLocaleDateString(),
    end: course.enddate !== 0 ? new Date(course.enddate * 1000).toLocaleDateString() : "Sin fecha de termino",
  });
  const [certificadoAvailable, setCertificadoAvailable] = React.useState(null);
  const [autoevaluacionAvailable, setAutoevaluacionAvailable] = React.useState(null);
  const [porcentajeInicial, setPorcentajeInicial] = React.useState("");
  const [porcentajeFinal, setPorcentajeFinal] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const chartRef = useRef();
  const [autoevaluacionData, setAutoevaluacionData] = React.useState({
    autoevaluacionInicial: [],
    autoevaluacionFinal: [],
  });

  useEffect(() => {
    if (modalOpen) {
      const timeout = setTimeout(() => {
        drawChart();
      }, 10);
      return () => clearTimeout(timeout);
    }
  }, [modalOpen, autoevaluacionData]);

  const transformAutoevaluacionData = (inicial, final) => {
    return inicial.map((item, index) => ({
      indicador: `${lang["History"].selfassessment_indicator} ${index + 1}`,
      inicial: item.value,
      final: final[index]?.value || 0,
    }));
  };

  const drawChart = () => {
    const dataAutoevaluacion = transformAutoevaluacionData(autoevaluacionData.autoevaluacionInicial, autoevaluacionData.autoevaluacionFinal);

    const minWidthPerItem = 100;
    const defaultWidth = 1080;

    const requiredWidth = dataAutoevaluacion.length * minWidthPerItem;
    const svgWidth = Math.max(requiredWidth, defaultWidth);

    // const svgWidth = 1080;
    const svgHeight = 400;
    const margin = { top: 20, right: 20, bottom: 50, left: 40 };
    const width = svgWidth - margin.left - margin.right;
    const height = svgHeight - margin.top - margin.bottom;

    d3.select(chartRef.current).selectAll("*").remove();

    const svg = d3.select(chartRef.current).attr("width", svgWidth).attr("height", svgHeight);

    const chart = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`);

    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "white")
      .style("border", "1px solid #ccc")
      .style("padding", "6px 8px")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-size", "12px")
      .style("max-width", "320px")
      .style("color", "#333")
      .style("box-shadow", "0px 2px 6px rgba(0,0,0,0.4)")
      .style("z-index", "9999")
      .style("opacity", 0);

    const x0 = d3
      .scaleBand()
      .domain(dataAutoevaluacion.map((d) => d.indicador))
      .rangeRound([0, width])
      .paddingInner(0.2);

    const x1 = d3.scaleBand().domain(["inicial", "final"]).rangeRound([0, x0.bandwidth()]).padding(0);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(dataAutoevaluacion, (d) => Math.max(d.inicial, d.final)) + 1])
      .nice()
      .rangeRound([height, 0]);

    const color = d3.scaleOrdinal().domain(["inicial", "final"]).range(["#156082", "#e97132"]);

    chart.append("g").attr("transform", `translate(0,${height})`).call(d3.axisBottom(x0));

    chart.append("g").call(d3.axisLeft(y).ticks(y.domain()[1]).tickFormat(d3.format("d")));

    const grupos = chart
      .selectAll("g.barra")
      .data(dataAutoevaluacion)
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(${x0(d.indicador)},0)`)
      .style("cursor", "pointer");

    grupos
      .selectAll("rect")
      .data((d) => [
        { key: "inicial", value: d.inicial },
        { key: "final", value: d.final },
      ])
      .enter()
      .append("rect")
      .attr("x", (d) => x1(d.key))
      .attr("y", y(0))
      .attr("width", x1.bandwidth())
      .attr("height", 0)
      .attr("fill", (d) => color(d.key))
      .on("mouseover", function (e, d) {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip.html(`<strong>${d.key}:</strong> ${d.value}`);
      })
      .on("mousemove", function (e) {
        tooltip.style("left", e.pageX + 10 + "px").style("top", e.pageY - 28 + "px");
      })
      .on("mouseout", function () {
        tooltip.transition().duration(500).style("opacity", 0);
      })
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attr("y", (d) => y(d.value))
      .attr("height", (d) => height - y(d.value));

    chart.append("g").call(d3.axisLeft(y).ticks(y.domain()[1]).tickFormat(d3.format("d")));

    chart
      .selectAll("line.horizontal")
      .data(y.ticks(y.domain()[1]))
      .enter()
      .append("line")
      .attr("class", "horizontal")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d))
      .attr("stroke", "#bfbfbf")
      .attr("stroke-width", 1)
      .lower();

    const xAxis = chart.append("g").attr("transform", `translate(0,${height})`).call(d3.axisBottom(x0));

    xAxis
      .selectAll("text")
      .style("cursor", "pointer")
      .on("mouseover", function (e, d) {
        const index = dataAutoevaluacion.findIndex((item) => item.indicador === d);
        const nombreCompleto = autoevaluacionData.nombres?.[index] || d;

        tooltip.transition().duration(200).style("opacity", 1);
        tooltip.html(`<strong>${nombreCompleto}</strong>`);
      })
      .on("mousemove", function (e) {
        tooltip.style("left", e.pageX + 10 + "px").style("top", e.pageY - 28 + "px");
      })
      .on("mouseout", function () {
        tooltip.transition().duration(500).style("opacity", 0);
      });

    const numPreguntas = dataAutoevaluacion.length;
    const valorMaximoPorPregunta = 5;
    const totalMaximo = numPreguntas * valorMaximoPorPregunta;

    const sumaInicial = d3.sum(dataAutoevaluacion, (d) => d.inicial);
    const sumaFinal = d3.sum(dataAutoevaluacion, (d) => d.final);

    const porcentajeInicial = Math.round((sumaInicial / totalMaximo) * 100);
    setPorcentajeInicial(porcentajeInicial);
    const porcentajeFinal = Math.round((sumaFinal / totalMaximo) * 100);
    setPorcentajeFinal(porcentajeFinal);

    //   const legend = svg.append("g").attr("transform", `translate(${svgWidth - margin.right - 100}, ${margin.top - 20})`);

    //   const legendData = [String("Inicial: " + porcentajeInicial + "%"), String("Final: " + porcentajeFinal + "%")];

    //   legend
    //     .selectAll("rect")
    //     .data(legendData)
    //     .enter()
    //     .append("rect")
    //     .attr("x", 0)
    //     .attr("y", (d, i) => i * 20)
    //     .attr("width", 15)
    //     .attr("height", 15)
    //     .attr("fill", (d) => color(d));

    //   legend
    //     .selectAll("text")
    //     .data(legendData)
    //     .enter()
    //     .append("text")
    //     .attr("x", 20)
    //     .attr("y", (d, i) => i * 20 + 12)
    //     .text((d) => d.charAt(0).toUpperCase() + d.slice(1))
    //     .style("font-size", "12px")
    //     .style("fill", "#333");
  };

  const fetchAutoevaluacionRespuestas = async (id, type) => {
    let data = await moodlev2.getFeedbackResponses(id);
    const transformed = data.responses.map((item) => ({
      value: Number(item.value),
    }));
    setAutoevaluacionData((prev) => ({
      ...prev,
      [type]: transformed,
    }));
  };

  useEffect(() => {
    const fetchSubmodules = async () => {
      let courseContent = await moodlev2.getCourseContentByID(course.id);
      // let foundAutoevalInicial = false;
      // let foundAutoevalFinal = false;

      // recorrer el course content y buscar el modulo que tenga certificado
      courseContent.modules &&
        courseContent.modules.forEach((module) => {
          if (module.modules && module.modules.length > 0) {
            module.modules.forEach(async (submodule) => {
              // si el modulo tiene el prefijo cert y es visible para el usuario
              if (submodule.name.toLowerCase().startsWith("cert") && submodule.uservisible) {
                console.log(submodule);
                setCertificadoAvailable(`${submodule.url}&downloadown=1`);
              }

              // si autoevaluación inicial completada
              if (submodule.uservisible && submodule.name.toLowerCase().startsWith("autoevaluación inicial") && submodule.isCompleted) {
                // foundAutoevalInicial = true;
                setAutoevaluacionAvailable(true);
                fetchAutoevaluacionRespuestas(submodule.instance, "autoevaluacionInicial");
                let data = await moodlev2.getFeedbackItems(submodule.instance);
                setAutoevaluacionData((prev) => ({
                  ...prev,
                  nombres: data.items.map((item) => item.name),
                }));
              }

              // Buscar autoevaluación final completada
              if (submodule.uservisible && submodule.name.toLowerCase().startsWith("autoevaluación final") && submodule.isCompleted) {
                // foundAutoevalFinal = true;
                setAutoevaluacionAvailable(true);
                fetchAutoevaluacionRespuestas(submodule.instance, "autoevaluacionFinal");
              }
            });
          }
        });

      // if (foundAutoevalInicial && foundAutoevalFinal) {
      //   setAutoevaluacionAvailable(true);
      // }
    };
    fetchSubmodules();
  }, [course.id]);

  const handleDetailNavigation = () =>
    navigation(`/listcourses/progress/${course.id}`, {
      state: { name: data.name },
    });

  const handleCourseNavigation = async () => {
    if (!certificadoAvailable) return;

    // La descarga necesita sesión web de Moodle: la cookie del iframe oculto de autologin
    // se bloquea como cookie de terceros, así que el login se hace en navegación de primer nivel.
    try {
      const { key } = await requestAutoLogin({ username: user.username });
      window.location.href = `${baseURL}auth/userkey/login.php?key=${key}&wantsurl=${encodeURIComponent(certificadoAvailable)}`;
    } catch (err) {
      console.error("No se pudo iniciar sesión para descargar el certificado:", err);
      window.location.href = certificadoAvailable;
    }
    /* navigation(`/detailcourse/${course.id}`, {
      state: { name: data.name },
    }); */
    // recorrer el course content y buscar el modulo que tenga certificado
  };

  const handleAutoevaluacion = async () => {
    if (!autoevaluacionAvailable) return;
    setModalOpen(true);
  };

  return (
    <>
      <tr>
        <th onClick={handleDetailNavigation} scope="row" className="title-row">
          {data.name}
        </th>
        <td>{grade}</td>
        <td>{data.start}</td>
        <td>{data.end === "Sin fecha de termino" ? lang["History"].fourth_column_answer : data.end}</td>
        <td style={{ cursor: certificadoAvailable && "pointer", fontWeight: certificadoAvailable ? 700 : 500 }} onClick={handleCourseNavigation}>
          {certificadoAvailable && <DownloadIcon />}
          {certificadoAvailable ? lang["History"].fifth_column_answer : "-"}
        </td>
        <td> - {/* {lang["History"].sixth_column_answer} */}</td>
        <td style={{ cursor: autoevaluacionAvailable && "pointer", fontWeight: autoevaluacionAvailable ? 700 : 500 }} onClick={handleAutoevaluacion}>
          {autoevaluacionAvailable && <VisibilityIcon />}
          {autoevaluacionAvailable ? lang["History"].seven_column_answer : "-"}
        </td>
      </tr>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} style={styles.modal}>
        <Box sx={styles.modalContent}>
          <div style={{ display: "flex", justifyContent: "end", right: "18px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#20a59cd4",
                color: "white",
                padding: "10px",
                borderRadius: "50%",
                cursor: "pointer",
              }}
              onClick={() => setModalOpen(false)}
            >
              <CloseIcon />
            </div>
          </div>
          <p style={styles.modalTitle}>{lang["History"].selfassessment_title}</p>
          <p style={styles.modalText}>{lang["History"].selfassessment_subtitle}</p>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px" }}>
              <div className="square" style={{ backgroundColor: "#156082", border: "1px solid rgba(0, 0, 0, 0.30)" }}></div>
              {lang["History"].selfassessment_initial + ": " + porcentajeInicial + "%"}
            </div>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px" }}>
              <div className="square" style={{ backgroundColor: "#e97132", border: "1px solid rgba(0, 0, 0, 0.30)" }}></div>
              <p>{lang["History"].selfassessment_final + ": " + porcentajeFinal + "%"}</p>{" "}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <svg ref={chartRef}></svg>
          </div>
          <ul style={styles.listAutoevaluacion}>
            <li style={styles.listAutoevaluacionText}>
              <div className="square" style={{ backgroundColor: "#156082", border: "1px solid rgba(0, 0, 0, 0.30)" }}></div>
              &nbsp;{lang["History"].selfassessment_initial_1}
            </li>
            <li style={styles.listAutoevaluacionText}>
              <div className="square" style={{ backgroundColor: "#e97132", border: "1px solid rgba(0, 0, 0, 0.30)" }}></div>
              &nbsp;{lang["History"].selfassessment_final_2}
            </li>
          </ul>
        </Box>
      </Modal>
    </>
  );
};

const styles = {
  modal: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    // position: "fixed",
    padding: "18px",
    backgroundColor: "white",
    borderRadius: "8px",
    maxWidth: "1280px",
    boxSizing: "border-box",
    overflowX: "auto",
    "&:focus": {
      outline: "none",
    },
  },
  modalTitle: {
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "8px",
  },
  modalText: {
    textAlign: "center",
    fontSize: "16px",
    marginBottom: "12px",
  },
  listAutoevaluacion: {
    listStyle: "none",
    display: "flex",
    margin: 0,
    padding: 0,
    justifyContent: "center",
    gap: "12px",
  },
  listAutoevaluacionText: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    fontSize: "16px",
    marginBottom: "18px",
    color: "#313131",
  },
};
